import { isValidDate, pad, parseDateOnlyParts } from "../utils/date";

const TIME_PARTS_REGEX = /T(\d{2}):(\d{2})/;
const TIMEZONE_REGEX = /(Z|[+-]\d{2}:?\d{2})$/;

const DISPLAY_LOCALE = "en-GB";
const DISPLAY_DATE_FORMATTER = new Intl.DateTimeFormat(DISPLAY_LOCALE, {
  timeZone: "UTC",
  year: "numeric",
  month: "short",
  day: "numeric",
});

const AUDIT_DATE_FORMATTER = new Intl.DateTimeFormat(DISPLAY_LOCALE, {
  year: "numeric",
  month: "short",
  day: "numeric",
});

const formatLocalTimezoneLabel = (date: Date) => {
  const offsetMinutes = -date.getTimezoneOffset();
  if (offsetMinutes === 0) return "UTC";

  const sign = offsetMinutes < 0 ? "-" : "+";
  const absoluteOffset = Math.abs(offsetMinutes);
  const hours = Math.floor(absoluteOffset / 60);
  const minutes = absoluteOffset % 60;
  const offset = minutes ? `${hours}:${pad(minutes)}` : `${hours}`;

  return `UTC${sign}${offset}`;
};

const formatLocalDisplayDate = (date: Date, fallback: string) => {
  if (!isValidDate(date)) return fallback;

  return AUDIT_DATE_FORMATTER.format(date);
};

export const formatDisplayDate = (
  input: string | number | Date | null | undefined,
  fallback = "-",
): string => {
  if (!input) return fallback;

  const parts = parseDateOnlyParts(input);
  if (!parts) return fallback;

  const utcDate = new Date(
    Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day)),
  );
  if (!isValidDate(utcDate)) return fallback;

  return DISPLAY_DATE_FORMATTER.format(utcDate);
};

export const formatDateTimestamp = (
  input: string | Date | null | undefined,
  fallback = "-",
  dateTimeSeparator = " ",
): string => {
  if (!input) return fallback;

  if (typeof input === "string") {
    const timeMatch = TIME_PARTS_REGEX.exec(input);
    const date = new Date(input);

    if (!timeMatch || !isValidDate(date)) {
      return fallback;
    }

    const formattedDate = formatLocalDisplayDate(date, fallback);
    const time = `${pad(date.getHours())}:${pad(date.getMinutes())}`;
    const timezone = TIMEZONE_REGEX.exec(input)?.[1];
    const timezoneLabel = timezone
      ? ` (${formatLocalTimezoneLabel(date)})`
      : "";

    return `${formattedDate}${dateTimeSeparator}${time}${timezoneLabel}`;
  }

  if (!isValidDate(input)) return fallback;

  const date = formatLocalDisplayDate(input, fallback);
  const time = `${pad(input.getHours())}:${pad(input.getMinutes())}`;

  return `${date}${dateTimeSeparator}${time} (${formatLocalTimezoneLabel(
    input,
  )})`;
};
